import { useState } from 'react';
import { motion } from 'framer-motion';
import { HiShoppingBag } from 'react-icons/hi';
import { isPremium } from '../utils/productMap';
import '../styles/Shop.css';

/**
 * One product tile.
 *
 * Lives here rather than inside Shop.js because the shop, the home page
 * strip and (later) related-product rails all need to look identical.
 * Copying the markup is how the category lists drifted apart.
 *
 * The parent owns what happens on click, so the same card works in a
 * grid that filters and in a rail that scrolls.
 */
export default function ProductCard({
  product,
  onZoom,
  onOpenDetails,
  onAddToCart,
  justAdded,
  compact = false,
}) {
  const [imageBroken, setImageBroken] = useState(false);

  const hasImage = product.image && !imageBroken;
  const added = justAdded?.id === product.id;

  return (
    <motion.div
      className={`shop-card ${compact ? 'shop-card-compact' : ''}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.3 }}
    >
      {/* IMAGE — click to zoom */}
      <div
        className={`shop-image ${hasImage && onZoom ? 'zoomable' : ''}`}
        onClick={() => hasImage && onZoom?.(product)}
        title={hasImage && onZoom ? 'Click to zoom' : undefined}
        /* Reachable without a mouse. Only when it actually does
           something — a card with no image isn't a control. */
        role={hasImage && onZoom ? 'button' : undefined}
        tabIndex={hasImage && onZoom ? 0 : undefined}
        aria-label={hasImage && onZoom ? `Zoom into ${product.name}` : undefined}
        onKeyDown={(e) => {
          if (hasImage && onZoom && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onZoom(product);
          }
        }}
      >
        {hasImage ? (
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            onError={() => setImageBroken(true)}
          />
        ) : (
          <div className="image-fallback">{product.emoji}</div>
        )}

        {/* Over the picture rather than in the tag list, so it still
            shows on a phone — where the tag pills are hidden to keep
            the cards short. Top-right: "Only N left" owns top-left. */}
        {isPremium(product) && <span className="premium-badge">Premium</span>}

        {!product.inStock ? (
          <span className="out-of-stock">Out of Stock</span>
        ) : (
          product.stock > 0 &&
          product.stock <= 5 && (
            <span className="low-stock">Only {product.stock} left</span>
          )
        )}
      </div>

      {/* INFO — tapping anywhere here opens the full details, since the
          card has to truncate the description. */}
      <div className="shop-info">
        <div
          className="shop-info-tap"
          onClick={() => onOpenDetails?.(product)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpenDetails?.(product);
            }
          }}
          aria-label={`View full details for ${product.name}`}
        >
          <h3>{product.name}</h3>

          <p className="planet-name">
            {product.emoji} {product.planet}
          </p>

          {!compact && <p className="description">{product.description}</p>}
        </div>

        <div className="price-box">
          <span className="price">₹{product.price}</span>
          {product.originalPrice && (
            <span className="old-price">₹{product.originalPrice}</span>
          )}
        </div>

        {!compact && product.tags?.length > 0 && (
          <div className="tags">
            {product.tags.map((tag) => (
              <span key={tag} className="tag">
                {tag}
              </span>
            ))}
          </div>
        )}

        <div className="shop-actions">
          <button
            className="view-details"
            onClick={() => onOpenDetails?.(product)}
          >
            View full details →
          </button>

          {/* The only action on the card now. "Buy Now" used to sit
              below it and jumped straight to checkout, which quietly
              skipped the cart — and with it the discount-code field. */}
          <button
            className={`cart-btn ${added ? 'added' : ''}`}
            disabled={!product.inStock}
            onClick={() => onAddToCart?.(product)}
          >
            {!product.inStock ? (
              'Unavailable'
            ) : added ? (
              'Added to Bag ✓'
            ) : (
              <>
                <HiShoppingBag />
                <span>Add to Bag</span>
              </>
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
